import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ZoomIn, ZoomOut, Pause, Volume2, Rabbit, Turtle, VolumeX } from "lucide-react";
import { SceneCanvas } from "../gameplay/SceneCanvas";
import { MissionCard } from "../design/MissionCard";
import { TokenCounter } from "../design/TokenCounter";
import { StreakBadge } from "../design/StreakBadge";
import { MascotBubble } from "../design/MascotBubble";
import { SpellingRibbon } from "../design/SpellingRibbon";
import { PauseOverlay } from "../design/PauseOverlay";
import { AccessibilityMenu } from "../design/AccessibilityMenu";
import { BigButton } from "../design/BigButton";
import { api, newIdempotencyKey } from "../lib/api";
import { useChildProfile } from "../contexts/ChildProfileContext";
import { useSettings } from "../contexts/SettingsContext";
import { HintChooser, requestHint, HINT_DURATIONS_MS } from "../gameplay/hints/HintOverlays";
import { speech } from "../audio/speechService";
import { sfx } from "../audio/sfx";

const CELEBRATIONS = [
  "Great job!",
  "Wonderful!",
  "You found it!",
  "Amazing searching!",
  "Nice work!",
  "Fantastic!",
];

function useOrientationScrim() {
  const [portrait, setPortrait] = useState(() =>
    typeof window !== "undefined" ? window.innerHeight > window.innerWidth : false,
  );
  useEffect(() => {
    const on = () => setPortrait(window.innerHeight > window.innerWidth);
    window.addEventListener("resize", on);
    window.addEventListener("orientationchange", on);
    return () => {
      window.removeEventListener("resize", on);
      window.removeEventListener("orientationchange", on);
    };
  }, []);
  return portrait;
}

export function GameplayScreen() {
  const { profileId, levelId } = useParams();
  const navigate = useNavigate();
  const { selected, refresh: refreshProfiles } = useChildProfile();
  const settings = useSettings();

  const [level, setLevel] = useState(null);
  const [objects, setObjects] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [foundIds, setFoundIds] = useState(new Set());
  const [tokenBalance, setTokenBalance] = useState(selected?.tokenBalance || 0);
  const [streak, setStreak] = useState(0);
  const [streakReset, setStreakReset] = useState(false);
  const [narrationLock, setNarrationLock] = useState(false);
  const [mascotText, setMascotText] = useState("");
  const [caption, setCaption] = useState("");
  const [spelling, setSpelling] = useState({ letters: [], active: -1, done: 0 });
  const [glowId, setGlowId] = useState(null);
  const [paused, setPaused] = useState(false);
  const [showA11y, setShowA11y] = useState(false);
  const [debug, setDebug] = useState(false);
  const [targetAnnouncedAt, setTargetAnnouncedAt] = useState(null);
  const [wrongTapsForCurrent, setWrongTapsForCurrent] = useState(0);
  const [hintOpen, setHintOpen] = useState(false);
  const [hintConfig, setHintConfig] = useState(null);
  const [hintOverlay, setHintOverlay] = useState(null);
  const [freeHintsRemaining, setFreeHintsRemaining] = useState(undefined);
  const hintOverlayTimerRef = useRef(null);
  // Per-session spelling audio toggle (Slow / Fast / Off). Seeded from the
  // child profile so the choice persists across levels. Any change is
  // PATCHed back to the profile so it also survives reload.
  const [spellingAudioMode, setSpellingAudioModeState] = useState(
    selected?.spellingAudioMode || "slow",
  );
  useEffect(() => {
    if (selected?.spellingAudioMode && selected.spellingAudioMode !== spellingAudioMode) {
      setSpellingAudioModeState(selected.spellingAudioMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);
  const [audioToggleToast, setAudioToggleToast] = useState(null);
  const audioToggleToastTimerRef = useRef(null);
  const transformRef = useRef(null);
  const portrait = useOrientationScrim();

  const nextTarget = useMemo(() => {
    return (objects || []).find((o) => !foundIds.has(o.id)) || null;
  }, [objects, foundIds]);

  // Load level + start session + pre-populate foundIds from server progress
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await api.get(`/levels/${levelId}`);
      if (cancelled) return;
      setLevel(data.level);
      setObjects(data.objects || []);

      // Pre-populate foundIds from server-side level_progress so a mid-level
      // reload doesn't reset the mission board. If ALL objects are already
      // found, route straight to the completion screen.
      let alreadyFoundIds = [];
      try {
        const pr = await api.get(`/levels/${data.level.id}/progress`, {
          params: { childProfileId: profileId },
        });
        alreadyFoundIds = Array.isArray(pr.data?.foundObjectIds) ? pr.data.foundObjectIds : [];
      } catch (_) {
        // 403/404 → treat as fresh
      }
      if (cancelled) return;
      if (alreadyFoundIds.length && data.objects?.length && alreadyFoundIds.length >= data.objects.length) {
        // Fully complete — skip the empty gameplay board entirely.
        navigate(`/child/${profileId}/complete/${data.level.id}`, {
          state: { level: data.level, objects: data.objects || [] },
          replace: true,
        });
        return;
      }
      if (alreadyFoundIds.length) {
        setFoundIds(new Set(alreadyFoundIds));
      }

      // Also load hint config for the chooser
      try {
        const app = await api.get("/health"); // sanity ping
        // hint config lives on app_settings — for now fetch via admin? Nope,
        // expose via a public endpoint minimally: piggyback known defaults.
      } catch (_e) { /* hint config falls back to defaults */ }
      setHintConfig({
        costs: { spoken_clue: 5, regional_sparkle: 10, warm_cold: 15, object_glow: 20 },
        freeHintsByDifficulty: { easy: 999, medium: 2, hard: 1 },
      });
      // Kick session
      const sess = await api.post("/gameplay/sessions", {
        childProfileId: profileId,
        levelId: data.level.id,
      });
      if (cancelled) return;
      setSessionId(sess.data.session.id);
      setTargetAnnouncedAt(new Date().toISOString());
    })();
    return () => {
      cancelled = true;
      speech.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelId, profileId]);

  // Announce the current target
  const announce = useCallback(
    async (obj) => {
      if (!obj) return;
      setTargetAnnouncedAt(new Date().toISOString());
      const line = `Can you find the ${obj.learningObject?.spokenName || obj.learningObject?.displayName || "object"}?`;
      setCaption(line);
      await speech.speak(line, {
        speed: settings.narrationSpeed,
        prerecordedUrl: obj.learningObject?.missionPromptAudioUrl || null,
      });
    },
    [settings.narrationSpeed],
  );

  // Announce first target once objects load
  useEffect(() => {
    if (sessionId && nextTarget && !narrationLock) {
      announce(nextTarget);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, nextTarget?.id]);

  const handleHit = useCallback(
    async (obj, tap) => {
      if (foundIds.has(obj.id)) return;
      setNarrationLock(true);
      setGlowId(obj.id);
      setWrongTapsForCurrent(0);
      try {
        const res = await api.post("/gameplay/correct-object", {
          childProfileId: profileId,
          levelId,
          levelObjectId: obj.id,
          sessionId,
          tapXPercent: tap.xPercent,
          tapYPercent: tap.yPercent,
          idempotencyKey: newIdempotencyKey(),
          targetAnnouncedAt,
        });
        const body = res.data;
        sfx.correct(settings.soundEffects === "on");
        setTokenBalance(body.tokenBalance);
        setStreak(body.currentStreak || 0);
        setStreakReset(false);
        if (body.milestoneReached) sfx.milestone(settings.soundEffects === "on");
        setFoundIds((prev) => new Set(prev).add(obj.id));
        // Celebration
        const phrase = CELEBRATIONS[Math.floor(Math.random() * CELEBRATIONS.length)];
        const displayName = obj.learningObject?.displayName || "";
        const spoken = obj.learningObject?.spokenName || displayName;
        setMascotText(`${phrase} You found the ${spoken}.`);
        setCaption(`${phrase} You found the ${spoken}.`);
        await speech.speak(`${phrase} You found the ${spoken}.`, {
          speed: settings.narrationSpeed,
        });
        // Spelling ribbon
        const letters = obj.learningObject?.spellingSequence || [];
        setSpelling({ letters, active: -1, done: 0 });
        await speech.speakLetters(letters, {
          speed: settings.narrationSpeed,
          spellingMode: spellingAudioMode,
          onLetter: ({ index }) =>
            setSpelling((s) => ({ letters: s.letters, active: index, done: index })),
        });
        setSpelling((s) => ({ letters: s.letters, active: -1, done: s.letters.length }));
        await speech.speak(spoken, { speed: settings.narrationSpeed });
      } catch (e) {
        // ignore transient errors — server is source of truth
      } finally {
        setGlowId(null);
        setMascotText("");
        setSpelling({ letters: [], active: -1, done: 0 });
        setNarrationLock(false);
      }
    },
    [foundIds, profileId, levelId, sessionId, settings, targetAnnouncedAt, spellingAudioMode],
  );

  // Detect level completion
  useEffect(() => {
    if (!objects.length) return;
    if (foundIds.size < objects.length) return;
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.post("/gameplay/complete-level", {
          childProfileId: profileId,
          levelId,
          sessionId,
          idempotencyKey: newIdempotencyKey(),
        });
        if (cancelled) return;
        navigate(`/child/${profileId}/complete/${levelId}`, {
          state: { result: res.data, level, objects },
          replace: true,
        });
      } catch (_e) { /* progress fetch is best-effort */ }
    })();
    return () => (cancelled = true);
  }, [foundIds, objects, sessionId, profileId, levelId, level, navigate]);

  const handleMiss = useCallback(
    async (tap) => {
      if (narrationLock) return;
      try {
        const res = await api.post("/gameplay/incorrect-tap", {
          childProfileId: profileId,
          levelId,
          sessionId,
          tapXPercent: tap.xPercent,
          tapYPercent: tap.yPercent,
        });
        sfx.incorrect(settings.soundEffects === "on");
        const gentle = res.data?.gentleMessage || "Keep going — you're doing great!";
        setMascotText(gentle);
        setCaption(gentle);
        setStreakReset(true);
        setStreak(0);
        setWrongTapsForCurrent((n) => n + 1);
        // Keep the mascot bubble visible long enough to be read even when
        // Web Speech API is silent (headless / muted browsers).
        const speakPromise = speech.speak(gentle, { speed: settings.narrationSpeed });
        const minVisible = new Promise((r) => setTimeout(r, 1600));
        await Promise.all([speakPromise, minVisible]);
      } catch (_) {
        /* server unreachable — swallow, UI stays gentle */
      } finally {
        setMascotText("");
      }
    },
    [narrationLock, profileId, levelId, sessionId, settings],
  );

  // After 3 consecutive misses on the same target, re-announce.
  useEffect(() => {
    if (wrongTapsForCurrent === 3 && nextTarget && !narrationLock) {
      const spoken = nextTarget.learningObject?.spokenName || "object";
      speech.speak(`Keep looking for the ${spoken}!`, { speed: settings.narrationSpeed });
    }
  }, [wrongTapsForCurrent, nextTarget, narrationLock, settings.narrationSpeed]);

  const speakerReplay = useCallback(() => {
    if (nextTarget) announce(nextTarget);
  }, [nextTarget, announce]);

  // Cycle Slow → Fast → Off → Slow. Also PATCH the child profile so the
  // choice survives navigation. Speech.cancel() prevents an in-flight
  // spelling ribbon from finishing at the old cadence.
  const SPELLING_MODE_ORDER = ["slow", "fast", "off"];
  const SPELLING_MODE_LABEL = {
    slow: "Spelling speed: Slow",
    fast: "Spelling speed: Fast",
    off: "Spelling muted",
  };
  const cycleSpellingAudioMode = useCallback(() => {
    const idx = SPELLING_MODE_ORDER.indexOf(spellingAudioMode);
    const next = SPELLING_MODE_ORDER[(idx + 1) % SPELLING_MODE_ORDER.length];
    setSpellingAudioModeState(next);
    setAudioToggleToast(SPELLING_MODE_LABEL[next]);
    if (audioToggleToastTimerRef.current) clearTimeout(audioToggleToastTimerRef.current);
    audioToggleToastTimerRef.current = setTimeout(() => {
      setAudioToggleToast(null);
      audioToggleToastTimerRef.current = null;
    }, 1600);
    speech.cancel();
    if (profileId) {
      api.patch(`/profiles/${profileId}`, { spellingAudioMode: next })
        .then(() => { refreshProfiles?.(); })
        .catch(() => { /* transient — local state is authoritative for this session */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spellingAudioMode, profileId, refreshProfiles]);
  useEffect(() => () => {
    if (audioToggleToastTimerRef.current) clearTimeout(audioToggleToastTimerRef.current);
  }, []);

  if (portrait) {
    return (
      <div className="wmleye-app portrait-scrim" data-testid="portrait-scrim">
        <div>
          <h1>Please rotate your device</h1>
          <p>The game plays best in landscape.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wmleye-app gameplay-screen" data-testid="gameplay-screen">
      <div
        className="gameplay-screen__rotate"
        role="alertdialog"
        aria-labelledby="rotate-heading"
        data-testid="gameplay-rotate-overlay"
      >
        <div className="gameplay-screen__rotate-inner">
          <div className="gameplay-screen__rotate-icon" aria-hidden="true">📱</div>
          <h2 id="rotate-heading">Please rotate your device</h2>
          <p>Turn your phone sideways to start searching!</p>
        </div>
      </div>
      <div className="gameplay-screen__topbar">
        <button
          className="gameplay-screen__pause"
          onClick={() => setPaused(true)}
          aria-label="Pause"
          data-testid="gameplay-pause"
        >
          <Pause size={22} />
        </button>
        {nextTarget && (
          <MissionCard
            thumbnail={
              nextTarget.missionThumbnailUrl ||
              nextTarget.learningObject?.thumbnailUrl
            }
            name={
              nextTarget.learningObject?.displayName ||
              nextTarget.learningObject?.canonicalName ||
              "target"
            }
            found={foundIds.size}
            total={objects.length}
            onSpeak={speakerReplay}
          />
        )}
        <div className="gameplay-screen__stats">
          <TokenCounter value={tokenBalance} />
          <StreakBadge
            streak={streak}
            visible={settings.showStreakCounter}
            resetPulse={streakReset}
          />
        </div>
      </div>

      <div className="gameplay-screen__stage">
        <SceneCanvas
          sceneImageUrl={level?.sceneImageUrl}
          objects={objects}
          foundIds={foundIds}
          onHit={handleHit}
          onMiss={handleMiss}
          showAllHitboxesDebug={debug}
          glowId={glowId}
          narrationLock={narrationLock}
          transformRef={transformRef}
          hintOverlay={hintOverlay}
          onPointerMovePercent={hintOverlay?.type === "warm_cold"
            ? (p) => setHintOverlay((prev) => (prev ? { ...prev, pointer: p } : prev))
            : undefined}
        />
      </div>

      <div className="gameplay-screen__bottom">
        <div className="gameplay-screen__bl">
          <BigButton variant="ghost" data-testid="gameplay-hint" onClick={() => setHintOpen(true)}>
            💡 Hint
          </BigButton>
        </div>
        <div className="gameplay-screen__bc">
          {spelling.letters.length > 0 && (
            <SpellingRibbon
              letters={spelling.letters}
              activeIndex={spelling.active}
              doneUpTo={spelling.done}
            />
          )}
          <div className="gameplay-screen__caption" data-testid="gameplay-caption">
            {caption}
          </div>
        </div>
        <div className="gameplay-screen__br">
          <button
            className="gameplay-screen__zoom"
            aria-label="Zoom in"
            onClick={() => transformRef.current?.zoomIn?.()}
            data-testid="gameplay-zoom-in"
          >
            <ZoomIn size={22} />
          </button>
          <button
            className="gameplay-screen__zoom"
            aria-label="Zoom out"
            onClick={() => transformRef.current?.zoomOut?.()}
            data-testid="gameplay-zoom-out"
          >
            <ZoomOut size={22} />
          </button>
          <button
            className="gameplay-screen__zoom"
            aria-label="Repeat clue"
            onClick={speakerReplay}
            data-testid="gameplay-replay"
          >
            <Volume2 size={22} />
          </button>
          <button
            className="gameplay-screen__zoom"
            aria-label={`Spelling audio mode (currently ${spellingAudioMode}). Tap to cycle Slow, Fast, or Off.`}
            onClick={cycleSpellingAudioMode}
            data-testid="gameplay-spelling-audio-toggle"
            data-mode={spellingAudioMode}
          >
            {spellingAudioMode === "slow" && <Turtle size={22} data-testid="gameplay-spelling-icon-slow" />}
            {spellingAudioMode === "fast" && <Rabbit size={22} data-testid="gameplay-spelling-icon-fast" />}
            {spellingAudioMode === "off" && <VolumeX size={22} data-testid="gameplay-spelling-icon-off" />}
          </button>
          <button
            className="gameplay-screen__zoom"
            aria-label="Accessibility"
            onClick={() => setShowA11y((v) => !v)}
            data-testid="gameplay-a11y"
          >
            A11y
          </button>
        </div>
      </div>

      {showA11y && (
        <div className="gameplay-screen__a11y">
          <AccessibilityMenu />
          <BigButton
            variant="ghost"
            onClick={() => setDebug((v) => !v)}
            data-testid="gameplay-debug-toggle"
          >
            {debug ? "Hide" : "Show"} hitboxes (debug)
          </BigButton>
        </div>
      )}

      {mascotText && (
        <div className="gameplay-screen__mascot">
          <MascotBubble text={mascotText} />
        </div>
      )}

      {audioToggleToast && (
        <div
          role="status"
          aria-live="polite"
          data-testid="gameplay-spelling-audio-toast"
          className="gameplay-screen__audio-toast"
        >
          {audioToggleToast}
        </div>
      )}

      <PauseOverlay
        open={paused}
        onResume={() => setPaused(false)}
        onRepeat={() => {
          setPaused(false);
          speakerReplay();
        }}
        onRestart={async () => {
          // Reset server-side progress so previously-found objects need to
          // be re-tapped, then reload to remount the gameplay screen fresh.
          try {
            await api.post("/gameplay/reset-level-progress", {
              childProfileId: profileId,
              levelId,
            });
          } catch (_) { /* keep UX unblocked even if reset fails */ }
          window.location.reload();
        }}
        onReturn={() =>
          navigate(`/child/${profileId}/worlds/${level?.worldId ? "" : ""}`)
        }
      />
      <HintChooser
        open={hintOpen}
        onClose={() => setHintOpen(false)}
        hintConfig={hintConfig}
        tokenBalance={tokenBalance}
        difficulty={settings.difficulty}
        freeHintsRemaining={freeHintsRemaining}
        onPicked={async (hintType) => {
          setHintOpen(false);
          if (!nextTarget) return;
          try {
            const res = await requestHint({
              profileId,
              levelId,
              sessionId,
              levelObjectId: nextTarget.id,
              hintType,
            });
            setTokenBalance(res.tokenBalance);
            setFreeHintsRemaining(res.freeHintsRemaining);

            // Visual overlay wiring: mount the overlay in the SceneCanvas
            // stage for the configured duration.
            if (hintOverlayTimerRef.current) {
              clearTimeout(hintOverlayTimerRef.current);
              hintOverlayTimerRef.current = null;
            }
            if (hintType === "object_glow" || hintType === "regional_sparkle" || hintType === "warm_cold") {
              const overlay = { type: hintType };
              if (hintType === "object_glow") overlay.objectHitbox = res.objectHitbox;
              if (hintType === "regional_sparkle") overlay.hintRegion = res.hintRegion;
              if (hintType === "warm_cold") {
                overlay.hintRegion = res.hintRegion;
                overlay.targetCenter = {
                  xPercent: nextTarget.xPercent + nextTarget.widthPercent / 2,
                  yPercent: nextTarget.yPercent + nextTarget.heightPercent / 2,
                };
                // Start the pointer neutral (scene center) so nothing shouts
                // "Hot!" before the child has moved.
                overlay.pointer = { xPercent: 50, yPercent: 50 };
              }
              setHintOverlay(overlay);
              hintOverlayTimerRef.current = setTimeout(() => {
                setHintOverlay(null);
                hintOverlayTimerRef.current = null;
              }, HINT_DURATIONS_MS[hintType] || 3000);
            }

            if (hintType === "spoken_clue" && res.spokenClueText) {
              setMascotText(res.spokenClueText);
              setCaption(res.spokenClueText);
              await speech.speak(res.spokenClueText, { speed: settings.narrationSpeed });
              setMascotText("");
            } else if (hintType === "regional_sparkle") {
              setCaption("Look near the sparkle!");
              await speech.speak("Look near the sparkle!", { speed: settings.narrationSpeed });
            } else if (hintType === "warm_cold") {
              setCaption("Move your finger — I'll tell you when you're getting warm!");
              await speech.speak("Move your finger to find warm and cold spots.", { speed: settings.narrationSpeed });
            } else if (hintType === "object_glow") {
              setCaption("Watch the glow!");
              await speech.speak("Watch the glow.", { speed: settings.narrationSpeed });
            }
          } catch (e) {
            const detail = e.response?.data?.detail;
            if (detail && detail.error === "insufficient_tokens") {
              setMascotText("Not enough tokens for this hint yet — try a cheaper one!");
              setTimeout(() => setMascotText(""), 2500);
            }
          }
        }}
      />
    </div>
  );
}
